import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import client from '../api/client';
import { getShowcaseItems, ShowcaseItem } from '../api/photoApi';
import { openCustomAlert } from '../components/common/useCustomAlert';
import './CreatePostPage.css';

const CLOUD_NAME = 'dpjvpheex';
const PRESET_NAME = 'gomting_preset';

const CreateShowcasePage = () => {
    const [image, setImage] = useState<File | null>(null);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [recentPhotos, setRecentPhotos] = useState<ShowcaseItem[]>([]);
    const [preview, setPreview] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    useEffect(() => () => {
        if(preview) {
            URL.revokeObjectURL(preview);
        }
    }, [preview]);

    useEffect(() => {
        getShowcaseItems().then((items) => setRecentPhotos([...items].filter((item) => /^https?:\/\/|^\/uploads\//.test(item.imageUrl)).sort((a, b) => b.id - a.id).slice(0, 3))).catch(() => setRecentPhotos([]));
    }, []);

    const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if(!file) {
            return;
        }
        setImage(file);
        setPreview(URL.createObjectURL(file));
        setError('');
    };

    const handleUpload = async () => {
        if(!image || !title.trim() || !description.trim()) {
            setError('사진, 제목, 사진에 얽힌 이야기를 모두 입력해 주세요.');
            return;
        }

        setIsLoading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('file', image);
            formData.append('upload_preset', PRESET_NAME);
            formData.append('cloud_name', CLOUD_NAME);

            const uploadUrl = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;
            const cloudinaryResponse = await axios.post(uploadUrl, formData);
            const imageUrl = cloudinaryResponse.data.secure_url;

            await client.post('/admin/showcase', {
                title: title.trim(),
                content: description.trim(),
                imageUrl,
                userId: localStorage.getItem('userId') || null
            });

            openCustomAlert({ message: '사진이 성공적으로 등록되었습니다.', type: 'success' });
            navigate('/board?tab=PHOTO');
        } catch (requestError: any) {
            console.error(requestError);
            const responseText = JSON.stringify(requestError?.response?.data || '');
            if(responseText.includes('whitelisted')) {
                setError(`설정 오류: Cloudinary 프리셋 '${PRESET_NAME}'을(를) Unsigned 모드로 설정해야 합니다.`);
            } else if(requestError?.response) {
                setError(`업로드 실패: ${requestError.response.data?.error?.message || '서버 오류'}`);
            } else {
                setError(`네트워크 오류: ${requestError?.message || '연결을 확인해주세요.'}`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <main className="create-page photo-create-page">
            <header className="create-intro">
                <p className="create-label">사진 제보</p>
                <h1 className="create-title">사진 아래 가려진 장면을 보여주세요.</h1>
                <p>한 장의 사진이 드러내는 사실과 맥락을 커뮤니티에 전해 주세요.</p>
                <div className="create-editorial"><h2>사진 이야기를 더 풍성하게</h2><ol><li>무엇을 찍었는지 제목으로 알려주세요.</li><li>언제, 어디서 찍었는지와 눈여겨볼 점을 적어주세요.</li><li>함께 보고 싶은 질문을 남기면 대화가 이어집니다.</li></ol></div>
                {recentPhotos.length > 0 && <div className="create-editorial create-recent"><h2>최근 공유된 사진</h2><div className="create-photo-samples">{recentPhotos.map((photo) => <a href={`#/board?tab=PHOTO&photo=${photo.id}`} key={photo.id}><img src={photo.imageUrl} alt={photo.title || `사진 #${photo.id}`} loading="lazy" /><span>{photo.title || `사진 #${photo.id}`}</span></a>)}</div></div>}
            </header>

            <section className="create-card" aria-labelledby="photo-field-label">
                <div className="create-field-heading">
                    <h2 id="photo-field-label" className="field-label">사진</h2>
                    <span>JPG, PNG, HEIC</span>
                </div>

                <button
                    type="button"
                    className={`photo-upload-section ${preview ? 'has-preview' : ''}`}
                    onClick={() => fileInputRef.current?.click()}
                >
                    {preview ? (
                        <img src={preview} alt="업로드할 사진 미리보기" className="photo-preview" />
                    ) : (
                        <span className="photo-upload-empty">
                            <strong>사진 선택</strong>
                            <span>보관함에서 선택하거나 직접 촬영하세요.</span>
                        </span>
                    )}
                </button>
                <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    className="photo-upload-input"
                    aria-label="사진 선택"
                    onChange={handleImageChange}
                />

                <div className="create-field photo-caption-field"><label className="field-label" htmlFor="photo-title">사진 제목</label><input id="photo-title" className="photo-caption-input" type="text" maxLength={80} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="사진 속에서 무엇을 발견했나요?" /></div>
                <div className="create-field photo-caption-field"><label className="field-label" htmlFor="photo-description">사진에 얽힌 이야기</label><textarea id="photo-description" className="photo-caption-textarea" maxLength={600} value={description} onChange={(event) => setDescription(event.target.value)} placeholder="찍은 장소와 상황, 다른 사람에게 묻고 싶은 점을 알려주세요." /><p className="field-help">{description.length}/600자 · 사진의 맥락이 있어야 다른 사람도 의견을 남길 수 있어요.</p></div>

                {error && <p className="create-error" role="alert">{error}</p>}

                <div className="create-actions">
                    <button type="button" className="create-secondary-button" onClick={() => navigate('/board?tab=PHOTO')}>취소</button>
                    <button type="button" className="create-button" onClick={handleUpload} disabled={isLoading}>
                        {isLoading ? '업로드 중…' : '사진 올리기'}
                    </button>
                </div>
            </section>
        </main>
    );
};

export default CreateShowcasePage;
